import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Card } from '../Components/ui/card';
import { TooltipProvider } from '../Components/ui/tooltip';
import { Badge } from '../Components/ui/badge';
import { cn } from '../lib/utils';
import { useTranslation } from 'react-i18next';

interface BehavioralInsightsTabProps {
  insights: any[];
}

const BehavioralInsightsTab: React.FC<BehavioralInsightsTabProps> = ({ insights }) => {
  const { t } = useTranslation();

  // Function to get area name from key
  const getAreaNameFromKey = (key: string): string => {
    const area = [
      {
        title: "Sleep",
        key: "sleep",
      },
      {
        title: "Social Support",
        key: "socialSupport",
      },
      {
        title: "Energy Level",
        key: "energyLevel",
      },
      {
        title: "Stress Management",
        key: "stressManagement",
      },
      {
        title: "Cognitive Function",
        key: "cognitiveFunction",
      },
      {
        title: "Emotional Regulation",
        key: "emotionalRegulation",
      }
    ].find(area => area.key === key);
    return area ? area.title : key;
  };

  // Function to get badge variant based on pattern type
  const getPatternBadgeStyle = (patternType: string) => {
    switch (patternType) {
      case "helpful":
      case "HELPFUL":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      case "hindering":
      case "HINDERING":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      case "neutral":
      case "NEUTRAL":
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };

  // Function to get pattern type label
  const getPatternTypeLabel = (patternType: string) => {
    switch (patternType) {
      case "helpful":
      case "HELPFUL":
        return "Helpful";
      case "hindering":
      case "HINDERING":
        return "Hindering";
      case "neutral":
      case "NEUTRAL":
        return "Neutral";
      default:
        return "Neutral";
    }
  };

  return (
    <TooltipProvider>
      <div className="mb-5 bg-amber-50 border border-amber-100 rounded-lg p-3 flex items-start gap-3">
        <HelpCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-amber-800 font-medium">{t('dashboard.healthInsights.howInsightsWork')}</p>
          <p className="text-sm text-amber-700">
            {t('dashboard.healthInsights.insightsExplanation')}
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {insights.map((insight, index) => (
          <Card key={index} className="overflow-hidden border-gray-200 hover:shadow-sm transition-all">
            <div className="p-4">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold text-gray-900">{insight.title}</h3>
                <Badge className={cn(getPatternBadgeStyle(insight.pattern_type), "ml-2")}>
                  {t(`dashboard.healthInsights.patternTypes.${insight.pattern_type?.toLowerCase() || 'neutral'}`)}
                </Badge>
              </div>
              
              <div className="mb-3 flex flex-wrap gap-2">
                {insight.tags.map((tag: string, idx: number) => (
                  <span key={idx} className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                    {getAreaNameFromKey(tag)}
                  </span>
                ))}
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-gray-700 font-medium">• {t('dashboard.healthInsights.pattern')}</p>
                  <p className="text-gray-600">{insight.pattern}</p>
                </div>
                
                <div>
                  <p className="text-gray-700 font-medium">• {t('dashboard.healthInsights.result')}</p>
                  <p className="text-gray-600">{insight.result}</p>
                </div>
                
                <div>
                  <p className="text-gray-700 font-medium">• {t('dashboard.healthInsights.suggestion')}</p>
                  <p className="text-gray-600">{insight.suggestion}</p>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </TooltipProvider>
  );
};

export default BehavioralInsightsTab;

